<template>
    <div
        class="Section HeroSection"
        :class="[variant, { 'has-overlay': overlay, 'has-background-image': backgroundImage }]"
        :style="backgroundImageStyle"
    >
        <div
            v-if="backgroundImage && typeof backgroundImage === 'object'"
            class="HeroBackgroundImage"
        >
            <picture>
                <source
                    v-if="backgroundImage.webp"
                    :srcset="backgroundImage.webp"
                    type="image/webp"
                />
                <source
                    v-if="backgroundImage.jpg"
                    :srcset="backgroundImage.jpg"
                    type="image/jpeg"
                />
                <img
                    :src="backgroundImage.src"
                    :alt="backgroundImage.alt || ''"
                    class="BackgroundImage"
                    :loading="backgroundImage.loading || 'eager'"
                    :decoding="backgroundImage.decoding || 'async'"
                />
            </picture>
        </div>
        <div v-if="overlay" class="HeroOverlay" :style="overlayStyle"></div>
        <div class="Content" :class="{ 'has-image': image }">
            <div class="HeroContent">
                <div class="TextContent">
                    <div v-if="badge" class="HeroBadge">
                        <slot name="badge">
                            <i v-if="badgeIcon" :class="badgeIcon"></i>
                            <span>{{ badge }}</span>
                        </slot>
                    </div>

                    <h1 class="HeroTitle" :lang="lang">
                        <slot name="title">
                            <span v-if="titleHighlight" class="TitleHighlight">{{
                                titleHighlight
                            }}</span>
                            <span v-html="title"></span>
                        </slot>
                    </h1>

                    <p v-if="subtitle" class="HeroSubtitle" :lang="lang">
                        <slot name="subtitle">{{ subtitle }}</slot>
                    </p>

                    <div v-if="hasActions" class="HeroActions">
                        <slot name="actions">
                            <router-link
                                v-if="primaryAction"
                                :to="primaryAction.to"
                                class="PrimaryAction"
                                @click.native="handlePrimaryClick"
                            >
                                <SiteButton>
                                    <span class="ButtonText">{{ primaryAction.text }}</span>
                                    <i
                                        v-if="primaryAction.icon"
                                        :class="primaryAction.icon + ' ButtonIcon'"
                                    ></i>
                                </SiteButton>
                            </router-link>
                            <a
                                v-if="secondaryAction"
                                :href="secondaryAction.href"
                                class="SecondaryAction"
                                @click="handleSecondaryClick"
                            >
                                <span>{{ secondaryAction.text }}</span>
                                <i v-if="secondaryAction.icon" :class="secondaryAction.icon"></i>
                            </a>
                        </slot>
                    </div>

                    <slot name="additional"></slot>
                </div>

                <div v-if="image" class="HeroVisual">
                    <div class="HeroImageContainer">
                        <picture class="HeroImage">
                            <source v-if="image.webp" :srcset="image.webp" type="image/webp" />
                            <source v-if="image.jpg" :srcset="image.jpg" type="image/jpeg" />
                            <img
                                :src="image.src"
                                :alt="image.alt || 'Hero Image'"
                                class="HeroImage"
                                :width="image.width"
                                :height="image.height"
                                :loading="image.loading || 'eager'"
                                :decoding="image.decoding || 'async'"
                            />
                        </picture>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { animateHero } from '@/utils/animations';
import SiteButton from '@/components/SiteButton';

export default {
    name: 'HeroSection',
    components: {
        SiteButton,
    },
    props: {
        variant: {
            type: String,
            default: 'default',
        },
        title: {
            type: String,
            default: '',
        },
        titleHighlight: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        badge: {
            type: String,
            default: '',
        },
        badgeIcon: {
            type: String,
            default: '',
        },
        primaryAction: {
            type: Object,
            default: null,
        },
        secondaryAction: {
            type: Object,
            default: null,
        },
        highlights: {
            type: Array,
            default: () => [],
        },
        image: {
            type: Object,
            default: null,
        },
        overlay: {
            type: [Boolean, String],
            default: false,
        },
        backgroundImage: {
            type: [String, Object],
            default: '',
        },
        animate: {
            type: Boolean,
            default: true,
        },
        animateTitleWords: {
            type: Boolean,
            default: false,
        },
        lang: {
            type: String,
            default: 'de',
        },
    },
    computed: {
        hasActions() {
            return this.$slots.actions || this.primaryAction || this.secondaryAction;
        },
        hasHighlights() {
            return this.$slots.highlights || (this.highlights && this.highlights.length > 0);
        },
        overlayStyle() {
            if (typeof this.overlay === 'string') {
                return { background: this.overlay };
            }
            return {};
        },
        backgroundImageStyle() {
            if (this.backgroundImage) {
                // If it's a string, use it as CSS background-image
                if (typeof this.backgroundImage === 'string') {
                    const backgroundUrl = this.backgroundImage.startsWith('url(')
                        ? this.backgroundImage
                        : `url(${this.backgroundImage})`;
                    return {
                        backgroundImage: backgroundUrl,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    };
                }
                // If it's an object, we'll use an img element instead
                // Return empty object, the img element will handle it
                return {};
            }
            return {};
        },
    },
    methods: {
        animateHero() {
            if (!this.animate) return null;

            const animation = animateHero({
                elements: {
                    title: '.HeroTitle',
                    titleHighlight: '.TitleHighlight',
                    subtitle: '.HeroSubtitle',
                    actions: '.HeroActions',
                    highlights: '.FeatureHighlights',
                    image: '.HeroImage',
                    badge: '.HeroBadge',
                },
                animateTitleWords: this.animateTitleWords,
            });

            this.$once('hook:beforeDestroy', () => {
                animation.kill();
            });

            return animation;
        },
        handlePrimaryClick() {
            if (this.primaryAction && this.primaryAction.onClick) {
                this.primaryAction.onClick();
            }
        },
        handleSecondaryClick() {
            if (this.secondaryAction && this.secondaryAction.onClick) {
                this.secondaryAction.onClick();
            }
        },
    },
    mounted() {
        if (this.animate) {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.animateHero();
                }, 100);
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.HeroSection {
    min-height: 90vh;
    color: var(--color-text-white);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;

    &.has-background-image {
        position: relative;
    }

    .HeroBackgroundImage {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        overflow: hidden;

        // Edge blur effect using gradient overlay
        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            pointer-events: none;
            // Radial gradient that creates a soft fade at edges
            background: radial-gradient(
                ellipse 90% 60% at 50% 50%,
                transparent 40%,
                rgba(0, 0, 0, 0.1) 60%,
                rgba(0, 0, 0, 0.3) 80%,
                rgba(0, 0, 0, 0.5) 100%
            );
            backdrop-filter: blur(15px);
            -webkit-backdrop-filter: blur(15px);
            // Mask to only show blur at edges
            mask-image: radial-gradient(
                ellipse 90% 60% at 50% 50%,
                transparent 50%,
                rgba(0, 0, 0, 0.5) 70%,
                rgba(0, 0, 0, 1) 100%
            );
            -webkit-mask-image: radial-gradient(
                ellipse 90% 60% at 50% 50%,
                transparent 50%,
                rgba(0, 0, 0, 0.5) 70%,
                rgba(0, 0, 0, 1) 100%
            );
        }

        .BackgroundImage {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            display: block;
            position: relative;
            z-index: 0;
            // Radial gradient mask for soft edge fade - creates vignette effect
            mask-image: radial-gradient(
                ellipse 85% 55% at 50% 50%,
                rgba(0, 0, 0, 1) 30%,
                rgba(0, 0, 0, 0.95) 45%,
                rgba(0, 0, 0, 0.8) 60%,
                rgba(0, 0, 0, 0.5) 75%,
                rgba(0, 0, 0, 0) 100%
            );
            -webkit-mask-image: radial-gradient(
                ellipse 85% 55% at 50% 50%,
                rgba(0, 0, 0, 1) 30%,
                rgba(0, 0, 0, 0.95) 45%,
                rgba(0, 0, 0, 0.8) 60%,
                rgba(0, 0, 0, 0.5) 75%,
                rgba(0, 0, 0, 0) 100%
            );
        }
    }

    &.has-overlay .HeroOverlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, rgba(0, 23, 50, 0.9) 0%, rgba(0, 0, 0, 0.65) 100%);
        z-index: 1;
    }

    &[style*='background-image'] {
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
    }

    @media (max-width: $breakpoint-tablet-portrait) {
        padding-top: 100px;
        min-height: unset;
    }

    .Content {
        @extend %sectionWrapper;
        position: relative;
        z-index: 2;
        padding: 0 20px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 1;
        margin: 140px auto 0;

        @media (max-width: $breakpoint-tablet-portrait) {
            margin: 40px auto 0;
        }

        &.has-image .HeroContent {
            display: flex;
            justify-content: space-between;
            align-items: center;

            @media (max-width: $breakpoint-tablet-landscape) {
                flex-direction: column;
                text-align: left;
                padding-top: 0px;
                padding-bottom: 50px;
            }
        }
    }

    .HeroContent {
        @media (max-width: $breakpoint-tablet-landscape) {
            text-align: left;
        }
    }

    .TextContent {
        position: relative;

        @media (max-width: $breakpoint-tablet-landscape) {
            width: 100%;
            margin-bottom: 60px;
        }
    }

    .HeroBadge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(10px);
        border: 0.5px solid rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.9);
        padding: 0.5rem 1.25rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        margin-bottom: 1.5rem;
    }

    .HeroTitle {
        font-size: 3.25rem;
        font-weight: 800;
        margin: 0 0 25px;
        line-height: 1.2;
        color: white;
        will-change: transform;
        text-transform: capitalize;

        @media (max-width: $breakpoint-tablet-landscape) {
            font-size: 3.2rem;
        }

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 2.5rem;
        }

        @media (max-width: $breakpoint-mobile) {
            font-size: 2rem;
            hyphens: auto;
            hyphenate-limit-chars: 6 3 2;
            hyphenate-limit-lines: 2;
            hyphenate-limit-zone: 8%;
            word-break: break-word;
            overflow-wrap: break-word;
        }
    }

    .HeroSubtitle {
        font-size: 1.25rem;
        line-height: 1.6;
        margin: 0 0 40px;
        color: rgba(255, 255, 255, 0.9);
        max-width: 600px;
        will-change: transform, opacity;

        @media (max-width: $breakpoint-mobile) {
            font-size: 1.125rem;
            hyphens: auto;
            hyphenate-limit-chars: 6 3 2;
            hyphenate-limit-lines: 2;
            hyphenate-limit-zone: 8%;
            word-break: break-word;
            overflow-wrap: break-word;
        }
    }

    .HeroActions {
        display: flex;
        gap: 20px;
        margin-bottom: 40px;
        will-change: transform, opacity;

        @media (max-width: $breakpoint-mobile) {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
        }

        .PrimaryAction {
            text-decoration: none;

            .SiteButton {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 16px 32px;
                font-size: 1.125rem;
                border-radius: 8px;
                background-color: #ffe89e;
                color: #2d3748;
                box-shadow: 0 10px 20px rgba(255, 232, 158, 0.3);
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 25px rgba(255, 232, 158, 0.4);
                    background-color: #f2d98a;

                    .ButtonIcon {
                        transform: translateX(5px);
                    }
                }

                .ButtonIcon {
                    transition: transform 0.3s ease;
                }

                @media (max-width: $breakpoint-mobile) {
                    width: 100%;
                    justify-content: center;
                    padding: 14px 20px;
                }
            }
        }

        .SecondaryAction {
            display: flex;
            align-items: center;
            gap: 8px;
            color: rgba(255, 255, 255, 0.9);
            text-decoration: none;
            font-weight: 500;
            font-size: 1.125rem;
            padding: 8px 16px;
            border-radius: 8px;
            transition: all 0.3s ease;

            svg {
                margin-top: 2px;
                transition: transform 0.3s ease;
            }

            &:hover {
                color: white;
                background-color: rgba(255, 255, 255, 0.3);

                svg {
                    transform: translateY(3px);
                }
            }

            @media (max-width: $breakpoint-mobile) {
                width: 100%;
                justify-content: flex-start;
            }
        }
    }

    .FeatureHighlights {
        display: flex;
        justify-content: center;
        margin-bottom: 40px;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        padding: 10px 16px;
        border-radius: 100px;
        border: 0.5px solid rgba(255, 255, 255, 0.1);
        max-width: 600px;
        gap: 24px;
        will-change: transform, opacity;

        &:hover {
            background: rgba(255, 255, 255, 0.15);
            transform: translateY(-3px);
        }

        @media (max-width: $breakpoint-mobile) {
            display: none;
            justify-content: center;
        }

        .FeatureItem {
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
            font-size: 0.9rem;

            .FeatureIcon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                background: rgba(255, 232, 158, 0.2);
                border-radius: 50%;

                svg {
                    color: #ffe89e;
                    width: 16px;
                    height: 16px;
                }
            }

            span {
                font-size: 0.9rem;
                font-weight: 500;
                white-space: nowrap;
                color: white;
            }
        }
    }

    .HeroVisual {
        width: 48%;
        position: relative;

        @media (max-width: $breakpoint-tablet-landscape) {
            width: 90%;
            max-width: 600px;
        }

        .HeroImageContainer {
            position: relative;
            z-index: 2;
            border-radius: 16px;
            overflow: hidden;
            border: 3px solid rgba(255, 232, 158, 0.3);
            box-shadow:
                0 30px 60px rgba(0, 0, 0, 0.3),
                0 15px 30px rgba(0, 0, 0, 0.2);
            transform: perspective(1000px) rotateY(-5deg) rotateX(5deg);
            transition: all 0.5s ease;

            &:hover {
                transform: perspective(1000px) rotateY(0deg) rotateX(0deg);
                box-shadow:
                    0 40px 70px rgba(0, 0, 0, 0.4),
                    0 20px 40px rgba(0, 0, 0, 0.3);
            }

            @media (max-width: $breakpoint-tablet-landscape) {
                transform: none;
                box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

                &:hover {
                    transform: none;
                }
            }
        }

        .HeroImage {
            width: 100%;
            height: auto;
            display: block;
            will-change: opacity;
        }
    }
}
</style>
